/**
 * Apply power distribution list edits from a Request Detail edit session on Save Changes (#996).
 * Mirrors largeUpsEditSessionFlush. Note PUT (not PATCH) for unit updates, and the
 * reorder body key is `powerDistributionIds`.
 */

import { api } from './api';
import {
  computeRepeatableOperations,
  type ComputedRepeatableOps,
} from './computeRepeatableOperations';
import { networkService } from './network';
import { offlineCache } from './offlineCache';
import { mirrorPowerDistributionToOfflineRequestCache } from './powerDistributionOfflineSync';
import { toCreatePowerDistributionPayload } from './powerDistributionPayload';
import { flushRepeatableInstanceAttachmentSectionRemaps } from './repeatableInstanceAttachmentRemapFlush';
import { deleteRepeatableUnitTolerating404 } from './repeatableUnitDelete';
import {
  LEGACY_POWER_DISTRIBUTION_SYNTHETIC_ID,
  validateSessionPowerDistributionNames,
} from '../components/powerdistribution/powerDistributionListUtils';
import { POWER_DISTRIBUTION_REPEATABLE_ATTACHMENT_BASE_SECTIONS } from '../utils/repeatableInstanceAttachmentSections';

import type { PowerDistributionConfiguration } from '../types/powerDistribution';

function isPersistedPowerDistributionId(id: number | undefined): id is number {
  return id != null && id > 0;
}

function isSessionOnlyPowerDistributionId(id: number | undefined): boolean {
  return id == null || id <= 0 || id === LEGACY_POWER_DISTRIBUTION_SYNTHETIC_ID;
}

/**
 * Canonicalize for content comparison: sort keys and drop undefined values so
 * two rows with identical CONTENT compare equal regardless of key insertion
 * order. Load-bearing since #997 Story 6.7 (mirror of `coolingSystemEditSessionFlush`):
 * the engine save bridge's baseline (raw API/mapper rows, identity keys first)
 * and the session rows (round-tripped through `descriptorInstanceToPowerDistribution`,
 * identity keys appended last) are different object lineages — a raw
 * `JSON.stringify` flagged every unit as changed, PUTting the whole list on save.
 */
function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(canonicalize);
  }
  if (value && typeof value === 'object') {
    const sorted: Record<string, unknown> = {};
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      const entry = (value as Record<string, unknown>)[key];
      if (entry !== undefined) {
        sorted[key] = canonicalize(entry);
      }
    }
    return sorted;
  }
  return value;
}

function powerDistributionContentKey(
  unit: PowerDistributionConfiguration,
): string {
  // `requestId` excluded too (#997 Story 6.7, mirror cooling/large UPS/rack
  // review): the engine conversion stamps `parseInt('o-…') → 0` on
  // offline-request session rows, which would keep every untouched unit
  // permanently "changed" against the cached baseline.
  const {
    id: _id,
    requestId: _rid,
    createdAt: _ca,
    updatedAt: _ua,
    ...rest
  } = unit;
  return JSON.stringify(canonicalize(rest));
}

function powerDistributionEqual(
  a: PowerDistributionConfiguration[],
  b: PowerDistributionConfiguration[],
): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every(
    (unit, index) =>
      powerDistributionContentKey(unit) ===
      powerDistributionContentKey(b[index]),
  );
}

export function powerDistributionEditSessionHasChanges(
  baseline: PowerDistributionConfiguration[],
  session: PowerDistributionConfiguration[],
): boolean {
  return !powerDistributionEqual(baseline, session);
}

/**
 * Pure save-time diff for power distribution units (#1501): consumed by this
 * flush AND by the aggregate save payload builder — one diff implementation,
 * two consumers.
 */
export function computePowerDistributionOperations(
  baseline: PowerDistributionConfiguration[],
  session: PowerDistributionConfiguration[],
): ComputedRepeatableOps<PowerDistributionConfiguration> {
  return computeRepeatableOperations(baseline, session, {
    isPersistedId: isPersistedPowerDistributionId,
    isSessionOnlyId: isSessionOnlyPowerDistributionId,
    contentKey: powerDistributionContentKey,
    stripForUpdate: unit => {
      const {
        id: _id,
        createdAt: _ca,
        updatedAt: _ua,
        requestId: _rid,
        ...data
      } = unit;
      return data;
    },
  });
}

export async function flushPowerDistributionEditSession(
  requestId: string,
  baseline: PowerDistributionConfiguration[],
  session: PowerDistributionConfiguration[],
  isOfflineRequest: boolean,
): Promise<PowerDistributionConfiguration[]> {
  if (!powerDistributionEditSessionHasChanges(baseline, session)) {
    return session;
  }

  const nameError = validateSessionPowerDistributionNames(session);
  if (nameError) {
    throw new Error(nameError);
  }

  const idKey = String(requestId);
  const useOnlineApi = networkService.isConnected() && !isOfflineRequest;

  const ops = computePowerDistributionOperations(baseline, session);

  for (const deletedId of ops.deletes) {
    if (useOnlineApi) {
      await deleteRepeatableUnitTolerating404(
        `/requests/${idKey}/power-distribution/${deletedId}`,
      );
    } else {
      await offlineCache.addPowerDistributionToOfflineQueue(
        idKey,
        'delete',
        undefined,
        deletedId,
      );
    }
  }

  const createdIdMap = new Map<number, number>();

  for (const { tempId, row } of ops.creates) {
    const payload = toCreatePowerDistributionPayload({
      ...row,
      requestId: parseInt(idKey, 10) || row.requestId,
    });
    if (useOnlineApi) {
      const created = await api.post<PowerDistributionConfiguration>(
        `/requests/${idKey}/power-distribution`,
        payload,
      );
      if (tempId != null && created.id) {
        createdIdMap.set(tempId, created.id);
      }
    } else {
      await offlineCache.addPowerDistributionToOfflineQueue(
        idKey,
        'create',
        payload,
        tempId,
      );
    }
  }

  for (const { id, data } of ops.updates) {
    if (useOnlineApi) {
      await api.put<PowerDistributionConfiguration>(
        `/requests/${idKey}/power-distribution/${id}`,
        data,
      );
    } else {
      await offlineCache.addPowerDistributionToOfflineQueue(
        idKey,
        'update',
        data,
        id,
      );
    }
  }

  const tempIdByClientKey = new Map(
    ops.creates.map(c => [c.clientKey, c.tempId]),
  );
  const orderedPersistedIds = ops.orderRefs
    .map(ref => {
      if (typeof ref === 'number') {
        return ref;
      }
      const tempId = tempIdByClientKey.get(ref);
      return tempId != null && createdIdMap.has(tempId)
        ? createdIdMap.get(tempId)!
        : null;
    })
    .filter((id): id is number => id != null);

  if (
    orderedPersistedIds.length > 0 &&
    JSON.stringify(orderedPersistedIds) !==
      JSON.stringify(ops.baselineOrderInSession)
  ) {
    if (useOnlineApi) {
      await api.patch<PowerDistributionConfiguration[]>(
        `/requests/${idKey}/power-distribution/reorder`,
        { powerDistributionIds: orderedPersistedIds },
      );
    } else {
      await offlineCache.addPowerDistributionToOfflineQueue(
        idKey,
        'reorder',
        undefined,
        undefined,
        orderedPersistedIds,
      );
    }
  }

  await flushRepeatableInstanceAttachmentSectionRemaps(
    idKey,
    createdIdMap,
    POWER_DISTRIBUTION_REPEATABLE_ATTACHMENT_BASE_SECTIONS,
    useOnlineApi,
  );

  const normalizedSession = session.map(unit => {
    if (unit.id != null && createdIdMap.has(unit.id)) {
      return { ...unit, id: createdIdMap.get(unit.id)! };
    }
    return unit;
  });

  if (!useOnlineApi) {
    await offlineCache.updateCachedRequestPowerDistribution(
      idKey,
      normalizedSession,
    );
  } else {
    await mirrorPowerDistributionToOfflineRequestCache(
      idKey,
      normalizedSession,
    );
  }

  return normalizedSession;
}
